import React from "react";

// A geomantic figure: four lines, each of one dot or two, drawn from the data
// and never from an image. The same geometry the ERB partial uses, because the
// server has to be able to draw this page without any of this file.
//
// A FIGURE IS MADE ONE LINE AT A TIME AND THAT IS HOW IT ARRIVES.
//   `revealed`      how many lines have landed, counting from the top
//   `landingLine`   the line arriving right now, drawn with its dots dropping
//                   in one after another
//   `sourceLine`    a line being READ OFF this figure to make another one —
//                   lit while the line it feeds is landing elsewhere
//
// A line that has not landed is drawn faintly rather than left out: an empty
// space that later fills is a layout that moves, and a shield that resizes
// under the cursor is unusable.
//
// THE DOTS ARE THE ONLY THING THAT ARRIVES. A cast records the parity of each
// row of points and nothing else — not how many points were made — so this
// animates the one dot or two that a row LEAVES, never a count of points
// nobody wrote down. The method page draws rows of points and says in its own
// caption that they are an illustration; here there is nothing to illustrate.
const WIDTH = 60;
const HEIGHT = 80;
const RADIUS = 5;
const PAIR = [16, 44];
const SINGLE = [30];

// How long after its line begins the second dot of a pair lands. A pair has to
// read as two things arriving, or "one dot or two" is a caption rather than
// something you watched happen.
const SECOND_DOT = 190;

export default function Figure({
  figure,
  size = 54,
  revealed = null,
  landingLine = null,
  sourceLine = null,
  beat = 0
}) {
  const lines = figure.lines || [];
  const height = Math.round((size * HEIGHT) / WIDTH);
  const label = `${figure.printedName}: ${lines
    .map((dots) => (dots === 2 ? "two dots" : "one dot"))
    .join(", ")}`;

  return (
    <svg
      className="geo-figure"
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      width={size}
      height={height}
      role="img"
      aria-label={label}
      data-testid={`geo-figure-${figure.slug}`}
    >
      {lines.map((dots, row) => {
        const landed = revealed === null || row < revealed;
        const landing = row === landingLine;
        const source = row === sourceLine;
        const xs = dots === 2 ? PAIR : SINGLE;

        const classes = ["geo-figure__line"];
        if (!landed && !landing) classes.push("geo-figure__line--waiting");
        if (landing) classes.push("geo-figure__line--landing");
        if (source) classes.push("geo-figure__line--source");

        return (
          <g key={row} className={classes.join(" ")}>
            {source ? (
              <rect
                className="geo-figure__band"
                x="2"
                y={10 + row * 20 - 9}
                width={WIDTH - 4}
                height="18"
                rx="3"
              />
            ) : null}
            {xs.map((x, index) => (
              <circle
                key={x}
                cx={x}
                cy={10 + row * 20}
                r={RADIUS}
                className={
                  landing
                    ? "geo-figure__dot geo-figure__dot--landing"
                    : landed
                      ? "geo-figure__dot geo-figure__dot--here"
                      : "geo-figure__dot"
                }
                // The second dot of a pair waits, so a pair is two arrivals
                // and not one wider mark.
                style={landing ? { animationDelay: `${index * SECOND_DOT}ms` } : undefined}
                data-beat={beat}
              />
            ))}
          </g>
        );
      })}
    </svg>
  );
}
